import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useCarePlanEvents } from '../../utils/reactQueryHooks';
import '../../styles/carePlanActivity.css';

const CarePlanActivity = ({ isOpen, onClose }) => {
    const [selectedFilter, setSelectedFilter] = useState('all');

    // Fetch care plan events
    const filters = useMemo(() => {
        const filterObj = {};
        if (selectedFilter !== 'all') {
            filterObj.execution_status = selectedFilter;
        }
        return filterObj;
    }, [selectedFilter]);
    
    const { data: carePlanEvents = [], isLoading, error } = useCarePlanEvents(filters);

    // Transform care plan events to match the expected format
    const activities = useMemo(() => {
        if (!Array.isArray(carePlanEvents)) {
            console.warn('carePlanEvents is not an array:', carePlanEvents);
            return [];
        }
        
        return carePlanEvents.map(event => ({
            id: event.event_id,
            timestamp: new Date(event.execution_timestamp),
            planName: event.care_plan_name,
            actionType: event.actuator_type,
            changeType: event.trigger_type,
            oldValue: event.previous_value,
            newValue: event.new_value,
            effectiveDate: event.effective_date,
            description: event.description,
            status: event.execution_status
        }));
    }, [carePlanEvents]);



    const handleFilterChange = (filter) => {
        setSelectedFilter(filter);
    };

    const formatTimestamp = (timestamp) => {
        try {
            // Handle different timestamp formats (Date object, string, number)
            let dateObj;
            
            if (timestamp instanceof Date) {
                dateObj = timestamp;
            } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
                dateObj = new Date(timestamp);
            } else {
                dateObj = new Date(timestamp);
            }
            
            // Validate the date is valid
            if (isNaN(dateObj.getTime())) {
                console.error('Invalid date in CarePlanActivity formatTimestamp:', timestamp);
                return 'Invalid Date';
            }
            
            return dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error('Error formatting timestamp in CarePlanActivity:', error, timestamp);
            return 'Invalid Date';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed':
                return 'ri-check-line';
            case 'failed':
                return 'ri-close-line';
            case 'pending':
                return 'ri-time-line';
            case 'executing':
                return 'ri-play-circle-line';
            default:
                return 'ri-question-line';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'warning';
            case 'executing':
                return 'executing';
            default:
                return 'neutral';
        }
    };

    const getActionTypeIcon = (actionType) => {
        switch (actionType) {
            case 'light':
                return 'ri-sun-line';
            case 'irrigation':
                return 'ri-water-flash-line';
            case 'fan':
                return 'ri-wind-line';
            case 'humidity':
                return 'ri-droplet-line';
            default:
                return 'ri-settings-line';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="care-plan-activity-overlay" onClick={onClose}>
            <div className="care-plan-activity-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Care Plan Activity</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                <div className="modal-content">
                    {/* Filter Controls */}
                    <div className="filter-controls">
                        <button 
                            className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('all')}
                        >
                            All Activities
                        </button>
                        <button 
                            className={`filter-btn ${selectedFilter === 'completed' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('completed')}
                        >
                            Completed
                        </button>
                        <button 
                            className={`filter-btn ${selectedFilter === 'failed' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('failed')}
                        >
                            Failed
                        </button>
                        <button 
                            className={`filter-btn ${selectedFilter === 'pending' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('pending')}
                        >
                            Pending
                        </button>
                    </div>

                    {/* Activities List */}
                    <div className="activities-list">
                        {isLoading ? (
                            <div className="loading-state">
                                <i className="ri-loader-4-line"></i>
                                <p>Loading activities...</p>
                            </div>
                        ) : error ? (
                            <div className="error-state">
                                <i className="ri-error-warning-line"></i>
                                <p>Error loading activities</p>
                                <small>{error.message}</small>
                            </div>
                        ) : activities.length === 0 ? (
                            <div className="empty-state">
                                <i className="ri-time-line"></i>
                                <p>No activities found</p>
                                <small>Care plan activities will appear here when they are executed</small>
                            </div>
                        ) : (
                            activities.map(activity => (
                                <div key={activity.id} className="activity-item">
                                    <div className="activity-header">
                                        <div className="activity-icon">
                                            <i className={getActionTypeIcon(activity.actionType)}></i>
                                        </div>
                                        <div className="activity-info">
                                            <h4>{activity.planName}</h4>
                                            <span className="activity-type">
                                                {activity.actionType} • {activity.changeType}
                                            </span>
                                        </div>
                                        <div className={`activity-status ${getStatusColor(activity.status)}`}>
                                            <i className={getStatusIcon(activity.status)}></i>
                                            <span>{activity.status}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="activity-details">
                                        <div className="activity-change">
                                            <span className="change-label">Value Change:</span>
                                            <span className="change-value">
                                                {activity.oldValue} → {activity.newValue}
                                            </span>
                                        </div>
                                        <div className="activity-meta">
                                            <span className="activity-time">
                                                {formatTimestamp(activity.timestamp)}
                                            </span>
                                            <span className="activity-date">
                                                Effective: {activity.effectiveDate}
                                            </span>
                                        </div>
                                        {activity.description && (
                                            <div className="activity-description">
                                                {activity.description}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

CarePlanActivity.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default CarePlanActivity; 